'use client';

import React, { useState, useEffect } from 'react';
import MuiAccordion from '@mui/material/Accordion';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Box from '@mui/material/Box';
import { styled } from '@mui/material';
import ErrorBoundary from '../ErrorBoundary';
import Link from '../Link';

import { NavigationItemProps } from '../NavigationItem';

import { ScrollSpy } from '../../utils/scrollSpy';
import ChevronIcon from '../ChevronIcon';

export interface TopicNavProps {
  currentCategoryId: string;
  navItems?: Array<NavigationItemProps>;
}

const TopicNav = ({ navItems, currentCategoryId = '' }: TopicNavProps) => {
  const [active, setActive] = useState(currentCategoryId);
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);

  useEffect(() => {
    // Expand top level nav item, if the initial currentCategoryId is a subnav item
    const expanded: string[] = [];
    if (!navItems) {
      setExpandedCategories(expanded);
      return;
    }

    const findParentNavItems = (navItems: NavigationItemProps[], currentCategoryId: string) => {
      let parentNavItems: string[] = [];
      navItems.forEach((navItem) => {
        if (navItem.id === currentCategoryId) {
          return;
        }
        if (navItem.subNavigation) {
          const found = navItem.subNavigation.find((subnav) => subnav.id === currentCategoryId);
          if (found) {
            parentNavItems.push(navItem.id!); // Add top-level parent
            return;
          } else {
            navItem.subNavigation.forEach((subnav) => {
              if ((subnav as any).subNavigation) {
                const foundSubSubNav = (subnav as any).subNavigation.find(
                  (subSubNav: any) => subSubNav.id === currentCategoryId
                );
                if (foundSubSubNav) {
                  parentNavItems.push(navItem.id!); // Add top-level parent
                  parentNavItems.push(subnav.id!); // Add second-level parent
                  return;
                }
              }
            });
          }
        }
      });
      return parentNavItems;
    };

    const parentNavItems = findParentNavItems(navItems, currentCategoryId);
    expanded.push(...parentNavItems);
    setExpandedCategories(expanded);
  }, [navItems, currentCategoryId]);

  const onScrollUpdate = (entry: { target: any; boundingClientRect: any }, isInViewPort: any) => {
    const { target, boundingClientRect } = entry;
    if (!target) return;

    const correspondingNavItem = document.querySelector(`[data-scrollspy-id="${target.id}"]`);
    const parentnavItemId = target.attributes['parent-category-id']?.value; // subNavigation will have this custom attr to point to the parent category

    if (!correspondingNavItem && !parentnavItemId) return;

    if (boundingClientRect.y <= 0 && isInViewPort) {
      if (parentnavItemId) {
        setActive(parentnavItemId);
      } else {
        setActive(target.id);
      }
    }
  };

  const expandCategory = (navItemId: string, parentNavItemId?: string) => (_evt: any, expanded: boolean) => {
    if (expanded) {
      if (parentNavItemId) {
        setExpandedCategories([parentNavItemId, navItemId]);
      } else {
        setExpandedCategories([navItemId]);
      }
      activateTopCategory(navItemId);
    } else {
      if (parentNavItemId) {
        setExpandedCategories([parentNavItemId]);
      } else {
        setExpandedCategories([]);
      }
    }
  };

  const activateTopCategory = (navItemId: string) => () => {
    setTimeout(() => {
      setActive(navItemId);
    }, 500);
  };

  return (
    <ErrorBoundary>
      <Root data-testid="TopicNav" displayPrint="none">
        <ScrollSpy handleScroll={onScrollUpdate} />
        <List data-testid="TopicNav-list" sx={{ p: 0, listStyle: 'none' }}>
          {navItems &&
            navItems.map((navItem, idx) => (
              <ListItem
                disablePadding
                key={`topicNav-${navItem.id ?? ''}`}
                sx={{ mb: 2.25 }}
                data-scrollspy-id={navItem.id}>
                {!!navItem?.subNavigation?.length ? (
                  <Accordion
                    elevation={0}
                    disableGutters
                    sx={{ width: '100%' }}
                    expanded={expandedCategories.includes(navItem.id!)}
                    onChange={expandCategory(navItem.id ?? '')}>
                    <AccordionSummary>
                      <AccordionTitle
                        sx={{ display: 'flex', alignItems: 'center', mb: 0 }}
                        data-scrollspy-id={navItem.id}
                        data-testid={`TopicNav-categoryAccordion-${navItem.id}`}>
                        <NavLink
                          {...(navItem as any)}
                          onClick={(evt: any) => {
                            evt.stopPropogation;
                            setActive(navItem.id ?? '');
                          }}
                          sx={{ display: 'flex', alignItems: 'center', pl: 1 }}
                          className={`top-category ${
                            (idx === 0 && !active) ||
                            active === navItem.id ||
                            navItem.subNavigation.some(
                              (subNavItem) =>
                                subNavItem.id === active ||
                                (subNavItem as any).subNavigation?.some(
                                  (subSubNavItem: any) => subSubNavItem.id === active
                                )
                            )
                              ? 'active'
                              : ''
                          }`}
                          data-testid={`TopicNav-categoryLink-${navItem.id}`}
                        />
                        <ChevronIcon sx={{ ml: 'auto' }} open={expandedCategories.includes(navItem.id!)} />
                      </AccordionTitle>
                    </AccordionSummary>

                    <AccordionDetails>
                      <List data-testid="TopicNav-list" sx={{ p: 0, listStyle: 'none' }}>
                        {navItem.subNavigation.map((subNavItem: any) => (
                          <ListItem disablePadding sx={{ p: 0, listStyle: 'none', mb: 1.5 }} key={subNavItem.id}>
                            {subNavItem.subNavigation?.length ? (
                              <Accordion
                                elevation={0}
                                disableGutters
                                sx={{ width: '100%' }}
                                expanded={expandedCategories.includes(subNavItem.id!)}
                                onChange={expandCategory(subNavItem.id!, navItem.id!)}>
                                <AccordionSummary>
                                  <AccordionTitle
                                    sx={{ display: 'flex', alignItems: 'center', mb: 0 }}
                                    data-scrollspy-id={subNavItem.id}
                                    data-testid={`TopicNav-categoryAccordion-${subNavItem.id}`}>
                                    <NavLink
                                      {...(subNavItem as any)}
                                      onClick={(evt: any) => {
                                        evt.stopPropogation;
                                        setActive(subNavItem.id ?? '');
                                      }}
                                      sx={{ display: 'flex', alignItems: 'center', pl: 2 }}
                                      className={`${
                                        (idx === 0 && !active) ||
                                        active === subNavItem.id ||
                                        subNavItem.subNavigation.map((item: any) => item.id).includes(active)
                                          ? 'subnav-active-expandable'
                                          : ''
                                      }`}
                                      data-testid={`TopicNav-categoryLink-${subNavItem.id}`}
                                    />
                                    <ChevronIcon
                                      sx={{ ml: 'auto' }}
                                      open={expandedCategories.includes(subNavItem.id!)}
                                    />
                                  </AccordionTitle>
                                </AccordionSummary>

                                <AccordionDetails>
                                  <List data-testid="TopicNav-list" sx={{ p: 0, listStyle: 'none' }}>
                                    {subNavItem.subNavigation.map((subSubNavItem: any, idx: number) => (
                                      <ListItem
                                        disablePadding
                                        sx={{
                                          p: 0,
                                          listStyle: 'none',
                                          mb: idx === subNavItem.subNavigation.length - 1 ? 0 : 1.5
                                        }}>
                                        <NavLink
                                          {...subSubNavItem}
                                          sx={{ pl: 4, mb: 0, mr: 2, display: 'block' }}
                                          onClick={() => setActive(subSubNavItem.id ?? '')}
                                          className={subSubNavItem.id === active ? 'subnav-active' : ''}
                                          data-testid={`TopicNav-subCategoryLink-${subSubNavItem.id}`}
                                        />
                                      </ListItem>
                                    ))}
                                  </List>
                                </AccordionDetails>
                              </Accordion>
                            ) : (
                              <NavLink
                                {...subNavItem}
                                sx={{ pl: 2, mb: 0, mr: 2, display: 'block' }}
                                onClick={() => setActive(subNavItem.id ?? '')}
                                className={subNavItem.id === active ? 'subnav-active' : ''}
                                data-testid={`TopicNav-subCategoryLink-${subNavItem.id}`}
                              />
                            )}
                          </ListItem>
                        ))}
                      </List>
                    </AccordionDetails>
                  </Accordion>
                ) : (
                  <NavLink
                    {...(navItem as any)}
                    onClick={() => setActive(navItem.id ?? '')}
                    sx={{ display: 'flex', alignItems: 'center', pl: 1 }}
                    className={`top-category ${(idx === 0 && !active) || active === navItem.id ? 'active' : ''}`}
                    data-testid={`TopicNav-categoryLink-${navItem.id}`}
                  />
                )}
              </ListItem>
            ))}
        </List>
      </Root>
    </ErrorBoundary>
  );
};

const Root = styled(Box, {
  name: 'TopicNav',
  slot: 'Root',
  overridesResolver: (_, styles) => [styles.root]
})(({ theme }) => ({
  position: 'sticky',
  top: theme.spacing(19),
  width: '100%'
}));

const NavLink = styled(Link, {
  name: 'TopicNav',
  slot: 'NavLink'
})(({ theme }) => ({
  ...theme.typography.smallText,
  'fontWeight': 500,
  'borderLeft': '2px solid transparent',

  'color': theme.palette.midnight.A100,

  '&:hover': {
    color: theme.palette.secondary.main,
    textDecoration: 'none'
  },

  '&.active': {
    borderLeftColor: theme.palette.secondary.main,
    color: theme.palette.secondary.main,
    fontWeight: 600
  },

  '&.subnav-active': {
    color: theme.palette.secondary.main,
    fontWeight: 600
  },

  '&.subnav-active-expandable': {
    // borderLeftColor: 'blue',
    color: theme.palette.secondary.main,
    fontWeight: 600
  }
}));

const AccordionTitle = styled(Box)(({ theme }) => ({
  ...theme.typography.smallText,
  'width': '100%',

  '& > div': {
    width: '100%'
  },

  '& > svg': {
    marginLeft: 'auto',
    maxWidth: '7.16px',
    minHeight: '1rem'
  }
}));

const Accordion = styled(MuiAccordion)({
  'backgroundColor': 'transparent',

  '&:before': {
    display: 'none'
  }
});

const AccordionSummary = styled(MuiAccordionSummary)(({ theme }) => ({
  ...theme.typography.smallText,
  'minHeight': 0,
  'padding': 0,
  'color': theme.palette.midnight.A70,
  'fontWeight': 500,
  'width': '100%',

  '&.Mui-expanded': {
    minHeight: 0,
    marginBottom: theme.spacing(2)
  },

  '& .MuiAccordionSummary-content': {
    margin: 0
  }
}));

const AccordionDetails = styled(MuiAccordionDetails)({
  'padding': 0,

  '& > a:last-child': {
    marginBottom: 0
  }
});

export default TopicNav;
